import {useMemo} from "react";
import {SeatMap, SeatMapData, TableGroup, TakenSeats} from "../types/event";
import {notify} from "../components/Notifications";
import {seatKey, seatKeySet} from "../lib/seats";

export function useSeatMapData(seatMap: SeatMap, selectedSeatGuids: string[]) {
    const parsedMap: SeatMapData | null = useMemo(() => {
        if (!seatMap?.json) return null;
        try {
            return JSON.parse(seatMap.json) as SeatMapData;
        } catch {
            notify.error("Error parsing seat map data.");
            return null;
        }
    }, [seatMap?.json]);

    const width = parsedMap?.size.width ?? 1000;
    const height = parsedMap?.size.height ?? 1000;

    const categoryColor = useMemo(() => {
        const byName: Record<string, string> = {};
        const categories = parsedMap?.categories ?? [];
        for (const c of categories) byName[c.name] = c.color;
        return byName;
    }, [parsedMap?.categories]);

    const taken: TakenSeats | undefined = seatMap.takenSeats;

    // Keyed on the case-insensitive seat key, not the raw guid: the backend stores a seat in
    // the layout's own spelling and normalises what a client sends to it, so a guid held here
    // can differ in case from the same seat coming back as sold or reserved. Matching raw
    // would draw an already-sold seat as free and let a buyer pick it.
    const soldSet = useMemo(() => seatKeySet(taken?.sold), [taken?.sold]);

    const reservedSet = useMemo(() => {
        const set = seatKeySet(taken?.reserved);
        // Our own holds are drawn as selected rather than as somebody else's reservation.
        for (const guid of selectedSeatGuids) set.delete(seatKey(guid));
        return set;
    }, [taken?.reserved, selectedSeatGuids]);

    const tableGroups = useMemo(() => {
        const groups: Record<string, TableGroup> = {};
        if (!parsedMap) return groups;

        for (const zone of parsedMap.zones ?? []) {
            const zoneKey = zone.name ?? "zone";
            for (const row of zone.rows ?? []) {
                const key = `${zoneKey}::${row.row_number}`;
                const seat = row.seats?.[0] as any;
                if (!seat?.seat_guid) continue;

                groups[key] ??= {tableSeatGuid: "", chairSeatGuids: [], chairCategory: "Standard"};

                if (seat.category === "Table" && seat.seat_number === row.row_number) {
                    groups[key].tableSeatGuid = seat.seat_guid;
                } else {
                    groups[key].chairSeatGuids.push(seat.seat_guid);
                }
            }
        }

        for (const k of Object.keys(groups)) {
            if (!groups[k].tableSeatGuid) delete groups[k];
        }

        return groups;
    }, [parsedMap]);

    const seatGuidToGroupKey = useMemo(() => {
        const map: Record<string, string> = {};
        for (const [k, g] of Object.entries(tableGroups)) {
            map[g.tableSeatGuid] = k;
            for (const c of g.chairSeatGuids) map[c] = k;
        }
        return map;
    }, [tableGroups]);

    return {
        parsedMap,
        width,
        height,
        categoryColor,
        soldSet,
        reservedSet,
        tableGroups,
        seatGuidToGroupKey,
    };
}
